import React from "react";
import { useNavigate } from "react-router-dom";
import { analytics } from "./utils/analytics";
import "./Hero.css";

const GRADE_SCALE = [
  { range: "90 – 100", grade: "A+", points: "4.0" },
  { range: "85 – 89", grade: "A", points: "4.0" },
  { range: "80 – 84", grade: "A-", points: "3.8" },
  { range: "75 – 79", grade: "B+", points: "3.4" },
  { range: "71 – 74", grade: "B", points: "3.0" },
  { range: "68 – 70", grade: "B-", points: "2.8" },
  { range: "64 – 67", grade: "C+", points: "2.4" },
  { range: "61 – 63", grade: "C", points: "2.0" },
  { range: "57 – 60", grade: "C-", points: "1.8" },
  { range: "53 – 56", grade: "D+", points: "1.4" },
  { range: "45 – 52", grade: "D", points: "1.0" },
  { range: "Below 45", grade: "F", points: "0.0" },
];

const Hero = () => {
  const navigate = useNavigate();

  const handleCalculateClick = () => {
    analytics.trackButtonClick("Calculate GPA");
    analytics.trackCalculatorStart();
    navigate("/calculator");
  };

  return (
    <main className="landing">
      <section className="hero" aria-label="UBIT and Karachi University GPA Calculator">
        <div className="hero__media" aria-hidden="true" />
        <div className="hero__veil" aria-hidden="true" />

        <div className="hero__content">
          <p className="hero__brand">UBIT GPA Calculator</p>
          <h1 className="hero__title">
            University of Karachi &amp; UBIT GPA Calculator for KU / UoK
          </h1>
          <p className="hero__lead">
            Fast, accurate GPA and CGPR for UBIT and all Karachi University
            departments — built on the official KU grading scale.
          </p>
          <div className="hero__actions">
            <button type="button" className="btn btn--primary" onClick={handleCalculateClick}>
              Calculate GPA
            </button>
            <a className="btn btn--ghost" href="#how-it-works">
              How KU GPA works
            </a>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="seo">
        <div className="seo__inner">
          <header className="seo__header">
            <h2>Karachi University GPA &amp; CGPR explained</h2>
            <p>
              This free <strong>University of Karachi GPA calculator</strong>{" "}
              (also called a <strong>KU GPA calculator</strong> or{" "}
              <strong>UoK GPA calculator</strong>) helps UBIT and other KU
              students convert marks into grade points and estimate semester GPA
              using the university grade point table.
            </p>
          </header>

          <div className="seo__grid">
            <article className="seo__block">
              <h3>Who this tool is for</h3>
              <p>
                Built for <strong>UBIT</strong> (Umaer Basha Institute of
                Information Technology) and students across the{" "}
                <strong>University of Karachi</strong>. Use it as a{" "}
                <strong>GPA calculator for KU</strong> when you want a quick
                check before results or while planning your semester.
              </p>
            </article>

            <article className="seo__block">
              <h3>How CGPR is calculated at KU</h3>
              <p>
                At University of Karachi, the cumulative average is often called{" "}
                <strong>CGPR</strong> (Cumulative Grade Point Ratio). The
                standard approach is:
              </p>
              <ol>
                <li>Convert each course score to its grade point.</li>
                <li>
                  Multiply grade points by credit hours (when credits differ).
                </li>
                <li>Divide total grade points by total credit hours.</li>
              </ol>
              <p className="seo__formula">
                CGPR = Total Grade Points ÷ Total Credit Hours
              </p>
            </article>
          </div>

          <div className="seo__table-wrap">
            <h3>Official-style University of Karachi grade point table</h3>
            <p className="seo__table-note">
              Marks bands and grade points used by this{" "}
              <strong>Karachi University GPA</strong> tool:
            </p>
            <div className="grade-table" role="table" aria-label="KU grade point table">
              <div className="grade-table__head" role="row">
                <span role="columnheader">Marks</span>
                <span role="columnheader">Grade</span>
                <span role="columnheader">Points</span>
              </div>
              {GRADE_SCALE.map((row) => (
                <div className="grade-table__row" role="row" key={row.grade + row.range}>
                  <span role="cell">{row.range}</span>
                  <span role="cell">{row.grade}</span>
                  <span role="cell">{row.points}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="seo__cta">
            <h3>Ready to check your GPA?</h3>
            <p>
              Open the calculator, enter your course scores, and get an instant
              estimate with the UBIT / University of Karachi GPA tool.
            </p>
            <button type="button" className="btn btn--primary" onClick={handleCalculateClick}>
              Open GPA Calculator
            </button>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <p>
          UBIT GPA Calculator · University of Karachi (KU / UoK) · Built by
          Muhammad Mubashir
        </p>
      </footer>
    </main>
  );
};

export default Hero;
